import React, { useState, useEffect } from 'react';
import { renderShowroomPreview } from '../../lib/showroomPreview';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
  Store, 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  MapPin, 
  Phone, 
  Mail, 
  MessageCircle,
  Users,
  TrendingUp,
  Eye,
  Heart,
  Star,
  Upload,
  Save,
  Camera,
  Video,
  ExternalLink,
  BarChart3
} from 'lucide-react';

// Gate 7 Phase D — Wave 1 / D-BIZ-2: canonical product categories (must match the
// backend enum in config/product_categories.py). Required when adding/editing products.
// The Marketplace category catalogue is PLATFORM-GLOBAL and lives in db.product_categories. This
// file used to carry its own copy of seven categories — and because that collection was empty, the
// client copy WAS the de-facto authority. It is now loaded from the canonical projection, so a
// category the platform governor adds or deprecates takes effect without a web release.
//
// The two canonical Marketplace item types. `subscription`/`bundle` are deliberately absent: no part
// of this domain supports them, and rendering a label a backend cannot store is how the retail-only
// UX drifted from the model in the first place.
const ITEM_TYPES = [
  { id: 'service', labelKey: 'pca.sm.itemTypeService' },
  { id: 'product', labelKey: 'pca.sm.itemTypeProduct' },
];

const ShowroomManager = () => {
  const { user } = useAuth();
  const { 
    products,
    showroom,
    showroomChecked,
    loading,
    error,
    createShowroom,
    updateShowroom,
    fetchShowroom,
    fetchBusinessProducts,
    geocodeAddress,
    createProduct,
    updateProduct,
    deleteProduct,
    uploadMedia,
    replyToInquiry,
    respondToReview,
    // Canonical category projection, already fetched by MarketplaceContext from
    // GET /marketplace/categories. Consumed rather than re-fetched so this screen cannot disagree
    // with the rest of the app about what the catalogue is. Null-safe: while it loads the select
    // simply has no options, instead of falling back to a stale local list that could offer a
    // category the backend would reject.
    categories,
    fetchCategories,
  } = useMarketplace();
  const { toast } = useToast();
  const { t } = useLanguage();

  // M13: persist product publish state (is_active) + delete via the canonical backend — no mock/local toggle.
  const handleToggleActive = async (product) => {
    const next = !(product.is_active !== false);   // current active unless explicitly false
    try {
      await updateProduct(product.id, { is_active: next });
      toast({ title: next ? t('pca.sm.productPublished') : t('pca.sm.productHidden'), description: next ? t('pca.sm.productNowActive', { name: product.name }) : t('pca.sm.productNowHidden', { name: product.name }) });
    } catch (e) {
      toast({ title: t('pca.sm.updateFailed'), description: e.message, variant: 'destructive' });
    }
  };

  const handleDeleteProduct = async (product) => {
    if (typeof window !== 'undefined' && !window.confirm(t('pca.sm.confirmDeleteProduct', { name: product.name }))) return;
    try {
      await deleteProduct(product.id);
      toast({ title: t('pca.sm.productDeleted'), description: t('pca.sm.productWasRemoved', { name: product.name }) });
    } catch (e) {
      toast({ title: t('pca.sm.deleteFailed'), description: e.message, variant: 'destructive' });
    }
  };

  // State
  const [showroomForm, setShowroomForm] = useState({
    name: '',
    description: '',
    location: { address: '', latitude: null, longitude: null, place_id: null },
    contact_options: {
      chat: true,
      email: '',
      phone: '',
      in_app_call: true
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Additional dialog states for showroom management
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [editProductOpen, setEditProductOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [uploadImagesOpen, setUploadImagesOpen] = useState(false);
  const [inquiriesOpen, setInquiriesOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  
  // Product form state
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    itemType: '',
    images: [],          // canonical M14 object keys sent to the backend (never blob/public URLs)
    imagePreviews: [],    // local blob URLs for display ONLY — never persisted
    video: '',            // M14: single canonical video object key (mp4/mov) sent to backend
    videoPreview: ''      // local blob URL for display ONLY — never persisted
  });
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoRemoved, setVideoRemoved] = useState(false); // edit: explicit clear of an existing product video

  // Additional states for inquiries and reviews
  const [inquiryReply, setInquiryReply] = useState('');
  const [reviewResponse, setReviewResponse] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [selectedReview, setSelectedReview] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isProcessingReply, setIsProcessingReply] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  
  // Product tab filter state — uses Tabs imports
  const [productTab, setProductTab] = useState('all');
  
  // Real inquiries/reviews require backend endpoints (Phase 4) — no fabricated records.
  // Honest-empty until then; the Inquiries/Reviews dialogs already render empty states.
  const [customerInquiries, setCustomerInquiries] = useState([]);
  const [customerReviews, setCustomerReviews] = useState([]);

  // Initialize form with existing showroom data or defaults
  useEffect(() => {
    if (showroom) {
      setShowroomForm({
        name: showroom.name || '',
        description: showroom.description || '',
        location: {
          address: showroom.location?.address || '',
          latitude: showroom.location?.latitude || null,
          longitude: showroom.location?.longitude || null,
          place_id: showroom.location?.place_id || null,
          formatted_address: showroom.location?.formatted_address || ''
        },
        contact_options: {
          chat: showroom.contact_options?.chat ?? true,
          email: showroom.contact_options?.email || '',
          phone: showroom.contact_options?.phone || '',
          in_app_call: showroom.contact_options?.in_app_call ?? true
        }
      });
    } else {
      // Ensure form is always properly initialized
      setShowroomForm({
        name: '',
        description: '',
        location: {
          address: '',
          latitude: null,
          longitude: null,
          place_id: null,
          formatted_address: ''
        },
        contact_options: {
          chat: true,
          email: '',
          phone: '',
          in_app_call: true
        }
      });
    }
  }, [showroom]);

  // Load business products when component mounts
  useEffect(() => {
    if (user?.brandId) {
      fetchBusinessProducts();
    }
  }, [user]);

  // Ask whether this tenant HAS a Showroom, on the same gate this screen already uses for its
  // products.
  //
  // It never asked. MarketplaceContext issues the only other fetchShowroom, and that effect is gated
  // on `userRole` being one of ['admin','manager','group-admin'] — a different condition from the
  // `user?.brandId` gate this screen runs on. For a tenant whose role is outside that list the fetch
  // never fired, so `showroomChecked` stayed false forever, `shouldShowCreateUI` could never become
  // true, and the screen rendered the full manager — Edit Showroom, Add Showroom Item — for a tenant
  // that owned no Showroom at all. Products loaded the whole time, because THEY were on the working
  // gate. That asymmetry let STGTEST1 author two orphaned listings on 2026-08-28.
  //
  // This is the same defect, and the same remedy, as the category fetch a few lines below: an
  // authoring surface must be self-sufficient rather than depending on another component's gate.
  // Idempotent, and the context still owns the one state.
  useEffect(() => {
    if (!user?.brandId || !fetchShowroom) return;
    fetchShowroom(user.brandId).catch(() => {
      /* resolved-with-error is handled by `error`; the screen must not claim a Showroom exists */
    });
  }, [user?.brandId]);

  // Load the canonical category catalogue for THIS screen.
  //
  // The category select was empty at runtime: this screen fetched its own products but relied on
  // MarketplaceContext's initialisation effect for categories, and that effect is gated on
  // `userRole` + `crmAuthenticated` — a different condition from the one that gates this screen.
  // When it did not run, no GET /marketplace/categories was ever issued (confirmed in the backend
  // request log: products/search present, categories absent) and the dropdown had nothing to show.
  //
  // Fetching here makes the authoring surface self-sufficient rather than dependent on another
  // component's gate. It is idempotent and cheap, and the context still owns the ONE state.
  useEffect(() => {
    if (!fetchCategories) return;
    if (Array.isArray(categories) && categories.length > 0) return;
    fetchCategories().catch(() => { /* the select stays empty rather than showing a stale list */ });
    // Keyed on identity only, deliberately: re-running on every `categories` change would loop
    // whenever the catalogue is genuinely empty.
  }, [user?.brandId]);

  // Debug: Track customer inquiries state changes
  useEffect(() => {
    console.log('Customer inquiries state changed (DETAILED):', customerInquiries.map(inq => ({
      id: inq.id,
      customer: inq.customer,
      status: inq.status,
      repliedAt: inq.repliedAt,
      reply: inq.reply,
      replyId: inq.replyId
    })));
  }, [customerInquiries]);

  // Debug: Track customer reviews state changes
  useEffect(() => {
    console.log('Customer reviews state changed:', customerReviews.map(rev => ({
      id: rev.id,
      customer: rev.customer,
      responded: rev.responded,
      respondedAt: rev.respondedAt
    })));
  }, [customerReviews]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data exists
    if (!showroomForm || typeof showroomForm !== 'object') {
      toast({
        title: t('pca.sm.formError'),
        description: t('pca.sm.formNotInit'),
        variant: "destructive"
      });
      return;
    }

    if (!showroomForm.name?.trim() || !showroomForm.description?.trim()) {
      toast({
        title: t('pca.sm.validationError'),
        description: t('pca.sm.fillRequiredFields'),
        variant: "destructive"
      });
      return;
    }

    // Validate user context
    if (!user?.brandId) {
      toast({
        title: t('pca.sm.authError'),
        description: t('pca.sm.businessIdRequiredLogin'),
        variant: "destructive"
      });
      return;
    }

    try {
      if (showroom && isEditing) {
        // Include business_id when updating showroom and validate all required fields
        const showroomDataWithBusinessId = {
          name: showroomForm.name,
          description: showroomForm.description,
          location: showroomForm.location || {},
          contact_options: showroomForm.contact_options || {},
          business_id: user.brandId
        };
        
        console.log('Updating showroom with data:', showroomDataWithBusinessId);
        console.log('Current showroom before update:', showroom);
        
        await updateShowroom(user.brandId, showroomDataWithBusinessId);
        
        // Refetch showroom data to ensure UI is updated
        try {
          await fetchShowroom(user.brandId);
        } catch (fetchError) {
          console.warn('Failed to refetch showroom after update:', fetchError);
        }
        
        setIsEditing(false);
        
        toast({
          title: t('pca.sm.showroomUpdated'),
          description: t('pca.sm.showroomUpdatedDesc')
        });
      } else {
        // Include business_id when creating showroom
        const showroomDataWithBusinessId = {
          ...showroomForm,
          business_id: user.brandId
        };
        
        await createShowroom(showroomDataWithBusinessId);
        toast({
          title: t('pca.sm.showroomCreated'),
          description: t('pca.sm.showroomCreatedDesc')
        });
        setShowCreateDialog(false);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: t('common.error'),
        description: error.message || t('pca.sm.failedSaveShowroom'),
        variant: "destructive"
      });
    }
  };

  // Additional handler functions for showroom management
  const handleAddProduct = async () => {
    console.log('handleAddProduct called with form:', productForm);
    console.log('Available functions:', { createProduct: !!createProduct, fetchBusinessProducts: !!fetchBusinessProducts });
    
    try {
      if (!productForm.name.trim() || !productForm.description.trim()) {
        toast({
          title: t('pca.sm.validationError'),
          description: t('pca.sm.productNameDescRequired'),
          variant: "destructive",
        });
        return;
      }

      // D-BIZ-2: category is required and must be canonical.
      if (!productForm.category) {
        toast({
          title: t('pca.sm.categoryRequired'),
          description: t('pca.sm.selectCategoryBeforeSaving'),
          variant: "destructive",
        });
        return;
      }

      if (!createProduct) {
        toast({
          title: t('pca.sm.functionNotAvailable'),
          description: t('pca.sm.productCreateFnUnavailable'),
          variant: "destructive",
        });
        return;
      }

      // Create product data with all required fields in correct format
      const productData = {
        name: productForm.name,
        description: productForm.description,
        category_id: productForm.category,
        // Real domain data, not a UI label: the backend persists this and validates it against the
        // category's applicability.
        item_type: productForm.itemType || undefined,
        location: { address: 'Showroom Location' },
        pricing: productForm.price ? { type: 'single', value: productForm.price } : undefined,
        tags: [],
        business_id: user?.brandId,
        showroom_id: showroom?.id || `showroom_${user?.brandId}`,
        images: productForm.images || [],
        video: productForm.video || undefined   // M14: single optional product video object key
      };

      console.log('Creating product with data:', productData);

      await createProduct(productData);
      console.log('Product created successfully');
      
      // Refresh products list
      if (fetchBusinessProducts && user?.brandId) {
        console.log('Refreshing products list...');
        await fetchBusinessProducts(user.brandId);
        console.log('Products list refreshed');
      }
      
      toast({
        title: t('pca.sm.productAddedTitle'),
        description: t('pca.sm.productAddedDesc', { name: productForm.name }),
      });

      // Reset form and close dialog
      setProductForm({ name: '', description: '', price: '', category: '', itemType: '', images: [], imagePreviews: [], video: '', videoPreview: '' });
      setAddProductOpen(false);

    } catch (error) {
      console.error('Product creation error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        productForm,
        user: user?.brandId,
        showroom: showroom?.id
      });
      
      toast({
        title: t('pca.sm.errorAddingProduct'),
        description: t('pca.sm.failedAddProduct', { error: error.message || t('pca.sm.unknownErrorOccurred') }),
        variant: "destructive",
      });
    }
  };

  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
  const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // aligned with backend M14 image cap

  const handleProductImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_IMAGE_TYPES.join(',');
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      const existing = productForm.images?.length || 0;
      if (existing + files.length > 10) {
        toast({ title: t('pca.sm.tooManyImages'), description: t('pca.sm.selectUpTo10'), variant: "destructive" });
        return;
      }
      // Frontend validation aligned with backend (truthful errors, no silent skip)
      for (const f of files) {
        if (!ALLOWED_IMAGE_TYPES.includes(f.type)) {
          toast({ title: t('pca.sm.unsupportedFile'), description: t('pca.sm.onlyImageTypes', { name: f.name }), variant: "destructive" });
          return;
        }
        if (f.size > MAX_IMAGE_BYTES) {
          toast({ title: t('pca.sm.fileTooLarge'), description: t('pca.sm.imageMaxSize', { name: f.name }), variant: "destructive" });
          return;
        }
      }
      if (files.length === 0) return;

      setUploadingImages(true);
      try {
        for (const file of files) {
          const preview = URL.createObjectURL(file);       // local preview ONLY — never persisted
          const objectKey = await uploadMedia(file);        // M14: upload-url -> signed PUT -> confirm -> object key
          setProductForm(prev => ({
            ...prev,
            images: [...(prev.images || []), objectKey],
            imagePreviews: [...(prev.imagePreviews || []), preview],
          }));
        }
        toast({ title: t('pca.sm.imagesUploaded'), description: t('pca.sm.imagesAdded', { count: files.length }) });
      } catch (err) {
        toast({ title: t('pca.sm.uploadFailed'), description: err.message || t('pca.sm.couldNotUploadImage'), variant: "destructive" });
      } finally {
        setUploadingImages(false);
      }
    };
    input.click();
  };

  // M14: single product video (mp4/mov). Aligned with backend ALLOWED_VIDEO + MAX_VIDEO_BYTES.
  const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
  const MAX_VIDEO_BYTES = 100 * 1024 * 1024; // aligned with backend M14 video cap (100MB)

  const handleProductVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = ALLOWED_VIDEO_TYPES.join(',');
    input.multiple = false;
    input.onchange = async (e) => {
      const file = (e.target.files || [])[0];
      if (!file) return;
      // Frontend validation aligned with backend (truthful errors, no silent skip)
      if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
        toast({ title: t('pca.sm.unsupportedFile'), description: t('pca.sm.onlyVideoTypes', { name: file.name }), variant: "destructive" });
        return;
      }
      if (file.size > MAX_VIDEO_BYTES) {
        toast({ title: t('pca.sm.fileTooLarge'), description: t('pca.sm.videoMaxSize', { name: file.name }), variant: "destructive" });
        return;
      }

      setUploadingVideo(true);
      try {
        const preview = URL.createObjectURL(file);      // local preview ONLY — never persisted
        const objectKey = await uploadMedia(file);       // M14: upload-url -> signed PUT -> confirm -> object key
        setProductForm(prev => ({ ...prev, video: objectKey, videoPreview: preview }));
        setVideoRemoved(false);
        toast({ title: t('pca.sm.videoUploaded'), description: t('pca.sm.productVideoAdded') });
      } catch (err) {
        toast({ title: t('pca.sm.uploadFailed'), description: err.message || t('pca.sm.couldNotUploadVideo'), variant: "destructive" });
      } finally {
        setUploadingVideo(false);
      }
    };
    input.click();
  };

  const handleRemoveProductVideo = () => {
    // Clearing here sends video:"" on update -> backend sets video to None. On create it just omits.
    setProductForm(prev => ({ ...prev, video: '', videoPreview: '' }));
    setVideoRemoved(true);
  };

  // Handle product editing
  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      price: product.pricing?.value || '',
      category: categories.some(c => c.id === product.category_id) ? product.category_id : '',
      itemType: product.item_type || '',
      images: [],            // keys for NEW uploads only; uploading replaces the product's media set
      imagePreviews: [],     // aligned with `images` (new uploads). Existing media shown read-only below.
      video: '',             // key for a NEW video upload only; empty = keep existing on save
      videoPreview: ''       // local preview for a newly uploaded video only
    });
    setVideoRemoved(false);
    setEditProductOpen(true);
  };

  const handleUpdateProduct = async () => {
    try {
      if (!selectedProduct || !productForm.name.trim() || !productForm.description.trim()) {
        toast({
          title: t('pca.sm.validationError'),
          description: t('pca.sm.productNameDescRequired'),
          variant: "destructive",
        });
        return;
      }

      // D-BIZ-2: category is required and must be canonical.
      if (!productForm.category) {
        toast({
          title: t('pca.sm.categoryRequired'),
          description: t('pca.sm.selectCategoryBeforeSaving'),
          variant: "destructive",
        });
        return;
      }

      if (!updateProduct) {
        toast({
          title: t('pca.sm.functionNotAvailable'),
          description: t('pca.sm.productUpdateFnUnavailable'),
          variant: "destructive",
        });
        return;
      }

      // Prepare update data. Only send images if the user uploaded NEW ones this session
      // (canonical object keys). Otherwise omit so the backend preserves existing media —
      // never resend signed read URLs (they expire) or blob/public URLs.
      const updateData = {
        name: productForm.name,
        description: productForm.description,
        category_id: productForm.category,
        // Real domain data, not a UI label: the backend persists this and validates it against the
        // category's applicability.
        item_type: productForm.itemType || undefined,
        pricing: productForm.price ? { type: 'single', value: productForm.price } : undefined,
      };
      if ((productForm.images || []).length > 0) {
        updateData.images = productForm.images;
      }
      // M14 single video: send new key if uploaded, send "" to clear if user removed the
      // existing one, otherwise omit so the backend preserves the current video.
      if (productForm.video) {
        updateData.video = productForm.video;
      } else if (videoRemoved) {
        updateData.video = "";
      }

      console.log('Updating product:', selectedProduct.id, 'with data:', updateData);

      // Call the backend API to update the product
      await updateProduct(selectedProduct.id, updateData);

      console.log('Product updated successfully');

      toast({
        title: t('pca.sm.productUpdated'),
        description: t('pca.sm.productUpdatedDesc', { name: productForm.name }),
      });

      // Reset form and close dialog
      setProductForm({ name: '', description: '', price: '', category: '', itemType: '', images: [], imagePreviews: [], video: '', videoPreview: '' });
      setVideoRemoved(false);
      setSelectedProduct(null);
      setEditProductOpen(false);

    } catch (error) {
      console.error('Product update error:', error);
      toast({
        title: t('pca.sm.errorUpdatingProduct'),
        description: t('pca.sm.failedUpdateProduct', { error: error.message || t('pca.sm.unknownError') }),
        variant: "destructive",
      });
    }
  };

  const handleViewAnalytics = () => {
    setAnalyticsOpen(true);
  };

  const handleUploadImages = () => {
    // REAL upload via uploadMedia (signed PUT → object key) — no fake success. Keys are staged into
    // showroomForm.images; full showroom-gallery persistence-on-save is finalized in Phase 4
    // (backend accepts images on showroom update; the save payload + load lifecycle are aligned there).
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length === 0) return;
      try {
        const keys = [];
        for (const file of files) {
          const objectKey = await uploadMedia(file);
          keys.push(objectKey);
        }
        setShowroomForm(prev => ({ ...prev, images: [...(prev.images || []), ...keys] }));
        toast({
          title: t('pca.sm.imagesUploadedTitle'),
          description: t('pca.sm.imagesUploadedComingSoon', { count: keys.length }),
        });
      } catch (err) {
        toast({
          title: t('pca.sm.uploadFailedTitle'),
          description: err.message || t('pca.sm.couldNotUploadImages'),
          variant: "destructive",
        });
      }
    };
    input.click();
  };

  const handleViewPublicProfile = () => {
    console.log('handleViewPublicProfile called with:', { showroom, user, products, productsLength: products?.length });
    
    const businessId = user?.brandId || showroom?.business_id;
    const showroomId = showroom?.id;
    
    if (!businessId && !showroomId) {
      toast({
        title: t('pca.sm.showroomNotAvailable'),
        description: t('pca.sm.showroomDataNotLoaded'),
        variant: "destructive",
      });
      return;
    }

    // Ensure we have fresh product data
    if (!products || products.length === 0) {
      console.log('No products found, fetching fresh data...');
      if (fetchBusinessProducts && user?.brandId) {
        fetchBusinessProducts().then(() => {
          console.log('Products refreshed, retrying profile view...');
          // Retry after fetching products
          setTimeout(() => handleViewPublicProfile(), 1000);
        }).catch(err => {
          console.error('Failed to fetch products:', err);
        });
        return;
      }
    }

    console.log('Proceeding with profile view. Products available:', products?.length || 0);

    // Create a simple in-app public preview instead of external URL
    const previewContent = `
# ${showroom?.name || 'Business Showroom'}

**Description:** ${showroom?.description || 'No description available'}

**Contact Information:**
- Email: ${showroom?.contact_options?.email || 'Not provided'}
- Phone: ${showroom?.contact_options?.phone || 'Not provided'}
- Location: ${showroom?.location?.address || 'Not provided'}

**Products & Services:** ${products?.length || 0} items available

---
This is a preview of your public showroom profile.
    `.trim();

    // Create a modal-like preview
    const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
    
    if (previewWindow) {
      // WEB-01: the preview is built with DOM APIs, not an HTML string. Tenant-authored values
      // (showroom name/description/contact/address, product name/description/price/category and
      // image/video URLs) reach the document only as textContent or attribute values, so markup in
      // any of them stays visible text instead of being parsed.
      renderShowroomPreview(previewWindow.document, { showroom, products, businessId, t });
      
      toast({
        title: t('pca.sm.publicProfilePreview'),
        description: t('pca.sm.showingPreview'),
      });
    } else {
      toast({
        title: t('pca.sm.previewBlocked'),
        description: t('pca.sm.popupBlocked'),
        variant: "default",
      });
    }
  };

  const handleCustomerInquiries = () => {
    setInquiriesOpen(true);
  };

  const handleRespondToReviews = () => {
    setReviewsOpen(true);
  };

  const handleUpdateDescriptions = () => {
    if (!showroom) {
      toast({
        title: t('common.error'),
        description: t('pca.sm.showroomDataNotAvailable'),
        variant: "destructive",
      });
      return;
    }

    // CRITICAL FIX: Populate showroomForm with current showroom data
    setShowroomForm({
      name: showroom.name || '',
      description: showroom.description || '',
      location: showroom.location || { address: '', latitude: null, longitude: null },
      contact_options: showroom.contact_options || { 
        email: '', 
        phone: '', 
        chat: false, 
        in_app_call: false 
      }
    });

    setIsEditing(true);
    toast({
      title: t('pca.sm.editModeEnabled'),
      description: t('pca.sm.editModeDesc', { action: t('pg.showroom.saveChanges') }),
    });
  };

  // Handler for replying to customer inquiries
  const handleReplyToInquiry = async (inquiryId) => {
    console.log('handleReplyToInquiry called with:', { inquiryId, inquiryReply, selectedInquiry });
    
    if (!inquiryReply.trim()) {
      toast({
        title: t('pca.sm.replyRequired'),
        description: t('pca.sm.enterReplyMessage'),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessingReply(true); // Prevent premature dialog close
      
      const inquiry = customerInquiries.find(inq => inq.id === inquiryId);
      if (!inquiry) {
        toast({
          title: t('pca.sm.inquiryNotFound'),
          description: t('pca.sm.couldNotFindInquiry'),
          variant: "destructive",
        });
        return;
      }

      console.log('Found inquiry:', inquiry);

      if (!replyToInquiry) {
        toast({
          title: t('pca.sm.functionNotAvailable'),
          description: t('pca.sm.replyFnUnavailable'),
          variant: "destructive",
        });
        return;
      }

      // Validate user context
      if (!user?.brandId) {
        toast({
          title: t('pca.sm.authError'),
          description: t('pca.sm.businessIdRequiredReplies'),
          variant: "destructive",
        });
        return;
      }

      // Call backend API to send the reply
      console.log('Sending reply to backend with data:', { inquiryId, inquiryReply, businessId: user.brandId });
      
      const result = await replyToInquiry(inquiryId, inquiryReply);
      console.log('Backend reply result:', result);

      // Update local state on success
      const updatedInquiries = customerInquiries.map(inq => 
        inq.id === inquiryId 
          ? { ...inq, status: 'replied', repliedAt: new Date().toISOString(), reply: inquiryReply, replyId: result.reply_id }
          : inq
      );
      
      console.log('Updating inquiries state:', {
        originalCount: customerInquiries.length,
        updatedCount: updatedInquiries.length,
        targetId: inquiryId,
        updatedInquiry: updatedInquiries.find(inq => inq.id === inquiryId)
      });
      
      setCustomerInquiries([...updatedInquiries]); // Force new array reference

      // Update selectedInquiry to reflect new status (fixes stale closure issue)
      const updatedSelectedInquiry = updatedInquiries.find(inq => inq.id === inquiryId);
      if (updatedSelectedInquiry) {
        setSelectedInquiry(updatedSelectedInquiry);
      }
      
      // Force re-render of dialog content
      setRefreshTrigger(prev => prev + 1);

      console.log('Reply sent successfully via backend with reply_id:', result.reply_id);
      
      toast({
        title: t('pca.sm.replySent'),
        description: t('pca.sm.replySentDesc', { customer: inquiry.customer }),
      });
      
      // Show updated status for 3 seconds before closing
      setTimeout(() => {
        setInquiryReply('');
        setSelectedInquiry(null);
        setIsProcessingReply(false);
      }, 3000); // 3 second delay to show updated badge
      
    } catch (error) {
      console.error('Reply error:', error);
      setIsProcessingReply(false); // Re-enable close on error
      toast({
        title: t('pca.sm.errorSendingReply'),
        description: t('pca.sm.failedSendReply', { error: error.message || t('pca.sm.unknownError') }),
        variant: "destructive",
      });
    }
  };

  // Handler for responding to customer reviews
  const handleRespondToReview = async (reviewId) => {
    console.log('handleRespondToReview called with:', { reviewId, reviewResponse, selectedReview });
    
    if (!reviewResponse.trim()) {
      toast({
        title: t('pca.sm.responseRequired'),
        description: t('pca.sm.enterResponseMessage'),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessingResponse(true); // Prevent premature dialog close
      
      const review = customerReviews.find(rev => rev.id === reviewId);
      if (!review) {
        toast({
          title: t('pca.sm.reviewNotFound'),
          description: t('pca.sm.couldNotFindReview'),
          variant: "destructive",
        });
        return;
      }

      console.log('Found review:', review);

      if (!respondToReview) {
        toast({
          title: t('pca.sm.functionNotAvailable'),
          description: t('pca.sm.responseFnUnavailable'),
          variant: "destructive",
        });
        return;
      }

      // Validate user context
      if (!user?.brandId) {
        toast({
          title: t('pca.sm.authError'),
          description: t('pca.sm.businessIdRequiredResponses'),
          variant: "destructive",
        });
        return;
      }

      // Call backend API to post the response
      console.log('Posting response to backend with data:', { reviewId, reviewResponse, businessId: user.brandId });
      
      const result = await respondToReview(reviewId, reviewResponse);
      console.log('Backend response result:', result);

      // Update local state on success
      const updatedReviews = customerReviews.map(rev => 
        rev.id === reviewId 
          ? { ...rev, responded: true, respondedAt: new Date().toISOString(), response: reviewResponse, responseId: result.response_id }
          : rev
      );
      
      console.log('Updating reviews state:', {
        originalCount: customerReviews.length,
        updatedCount: updatedReviews.length,
        targetId: reviewId,
        updatedReview: updatedReviews.find(rev => rev.id === reviewId)
      });
      
      setCustomerReviews([...updatedReviews]); // Force new array reference

      // Update selectedReview to reflect new status (fixes stale closure issue)
      const updatedSelectedReview = updatedReviews.find(rev => rev.id === reviewId);
      if (updatedSelectedReview) {
        setSelectedReview(updatedSelectedReview);
      }
      
      // Force re-render of dialog content
      setRefreshTrigger(prev => prev + 1);

      console.log('Response posted successfully via backend with response_id:', result.response_id);
      
      toast({
        title: t('pca.sm.responsePosted'),
        description: t('pca.sm.responsePostedDesc', { customer: review.customer }),
      });
      
      // Show updated status for 3 seconds before closing
      setTimeout(() => {
        setReviewResponse('');
        setSelectedReview(null);
        setIsProcessingResponse(false);
      }, 3000); // 3 second delay to show updated badge
      
    } catch (error) {
      console.error('Response error:', error);
      setIsProcessingResponse(false); // Re-enable close on error
      toast({
        title: t('pca.sm.errorPostingResponse'),
        description: t('pca.sm.failedPostResponse', { error: error.message || t('pca.sm.unknownError') }),
        variant: "destructive",
      });
    }
  };

  const handleLocationLookup = async () => {
    // Validate address input
    if (!showroomForm?.location?.address?.trim()) {
      toast({
        title: t('pca.sm.addressRequired'),
        description: t('pca.sm.enterAddressLookup'),
        variant: "destructive"
      });
      return;
    }

    try {
      // Check if geocodeAddress function is available
      if (typeof geocodeAddress !== 'function') {
        throw new Error('Location service not available');
      }

      const locationData = await geocodeAddress(showroomForm.location.address);
      
      // Validate response data
      if (!locationData || typeof locationData !== 'object') {
        throw new Error('Invalid location response');
      }

      setShowroomForm(prev => ({
        ...prev,
        location: {
          ...prev.location,
          latitude: locationData.latitude || null,
          longitude: locationData.longitude || null,
          place_id: locationData.place_id || null,
          formatted_address: locationData.formatted_address || locationData.address || prev.location.address
        }
      }));
      
      toast({
        title: t('pca.sm.locationFound'),
        description: t('pca.sm.locationVerifiedUpdated')
      });
    } catch (error) {
      console.error('Location lookup error:', error);
      toast({
        title: t('pca.sm.locationError'),
        description: error.message || t('pca.sm.failedFindLocation'),
        variant: "destructive"
      });
    }
  };

  const calculateAnalytics = () => {
    const totalViews = products.reduce((sum, product) => sum + (product.views || 0), 0);
    const totalProducts = products.length;
    const activeProducts = products.filter(p => p.is_active).length;
    // Derived from real reviews (0 when none) — no fabricated rating.
    const avgRating = customerReviews.length
      ? Number((customerReviews.reduce((s, r) => s + (r.rating || 0), 0) / customerReviews.length).toFixed(1))
      : 0;

    return {
      totalViews,
      totalProducts,
      activeProducts,
      avgRating
    };
  };

  const analytics = calculateAnalytics();

  // Filter products by tab
  const filteredProducts = (products || []).filter(p => {
    if (productTab === 'all') return true;
    if (productTab === 'active') return p.is_active !== false;
    if (productTab === 'hidden') return p.is_active === false;
    return true;
  });

  console.log('ShowroomManager render state:', { 
    showroom, 
    loading, 
    hasShowroom: !!showroom,
    showroomId: showroom?.id,
    businessId: user?.brandId 
  });
  
  // Only show create showroom UI once a fetch has DEFINITIVELY confirmed there is no showroom
  // (showroomChecked = a 200 or 404 has resolved). This prevents the "flash correct page then flip
  // to create" bug where an in-flight/racing fetch momentarily left showroom null.
  const shouldShowCreateUI = showroomChecked && !showroom && !loading && user?.brandId && !error;

  // Whether a canonical Showroom actually exists. `shouldShowCreateUI` answers a narrower question
  // ("are we certain enough to render the create screen") and is deliberately conservative about
  // races; this answers "may we offer Showroom-scoped actions at all". They are not the same, and
  // conflating them is what put Edit Showroom and Add Showroom Item in front of a tenant with no
  // Showroom: every state that is not a CONFIRMED existing Showroom must withhold those actions,
  // including the not-yet-resolved one.
  const hasShowroomRecord = !!showroom;
  
  // Add debugging to help track showroom state
  useEffect(() => {
    console.log('ShowroomManager state:', {
      showroom: !!showroom,
      loading,
      userBusinessId: user?.brandId,
      error,
      shouldShowCreateUI
    });
  }, [showroom, loading, user?.brandId, error, shouldShowCreateUI]);
  
  if (shouldShowCreateUI) {
    // Show create showroom UI
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="w-full max-w-2xl mx-4">
          <CardHeader className="text-center">
            <Store className="mx-auto h-12 w-12 text-blue-600 mb-4" />
            <CardTitle className="text-2xl">{t('pg.showroom.createTitle')}</CardTitle>
            <p className="text-gray-600">
              {t('pg.showroom.createSubtitle')}
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('pg.showroom.basicInfo')}</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-1">{t('pg.showroom.businessNameReq')}</label>
                  <Input
                    value={showroomForm.name}
                    onChange={(e) => setShowroomForm(prev => ({...prev, name: e.target.value}))}
                    placeholder={t('pg.showroom.enterBusinessName')}
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{t('pg.showroom.descriptionReq')}</label>
                  <Textarea
                    value={showroomForm.description}
                    onChange={(e) => setShowroomForm(prev => ({...prev, description: e.target.value}))}
                    placeholder={t('pg.showroom.describeBusiness')}
                    rows={4}
                    required
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('pg.showroom.location')}</h3>

                <div>
                  <label className="block text-sm font-medium mb-1">{t('pg.showroom.businessAddress')}</label>
                  <div className="flex space-x-2">
                    <Input
                      value={showroomForm.location.address}
                      onChange={(e) => setShowroomForm(prev => ({
                        ...prev, 
                        location: {...prev.location, address: e.target.value}
                      }))}
                      placeholder={t('pg.showroom.enterAddress')}
                      className="flex-1"
                    />
                    <Button type="button" onClick={handleLocationLookup} variant="outline">
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                  {showroomForm.location.latitude && (
                    <p className="text-xs text-green-600 mt-1">
                      {t('pg.showroom.locationVerified')}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Options */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">{t('pg.showroom.contactOptions')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('pg.showroom.email')}</label>
                    <Input
                      type="email"
                      value={showroomForm.contact_options.email}
                      onChange={(e) => setShowroomForm(prev => ({
                        ...prev,
                        contact_options: {...prev.contact_options, email: e.target.value}
                      }))}
                      placeholder="business@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">{t('pg.showroom.phone')}</label>
                    <Input
                      value={showroomForm.contact_options.phone}
                      onChange={(e) => setShowroomForm(prev => ({
                        ...prev,
                        contact_options: {...prev.contact_options, phone: e.target.value}
                      }))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={showroomForm.contact_options.chat}
                      onChange={(e) => setShowroomForm(prev => ({
                        ...prev,
                        contact_options: {...prev.contact_options, chat: e.target.checked}
                      }))}
                      className="rounded"
                    />
                    <span className="text-sm">{t('pg.showroom.enableChat')}</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={showroomForm.contact_options.in_app_call}
                      onChange={(e) => setShowroomForm(prev => ({
                        ...prev,
                        contact_options: {...prev.contact_options, in_app_call: e.target.checked}
                      }))}
                      className="rounded"
                    />
                    <span className="text-sm">{t('pg.showroom.enableCalling')}</span>
                  </label>
                </div>
              </div>

              <div className="flex space-x-4 pt-6 border-t">
                <Button type="submit" disabled={loading} className="flex-1">
                  {loading ? t('pca.sm.creating') : t('pg.showroom.createShowroom')}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show existing showroom management UI
  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Store className="mr-3 h-6 w-6" />
              {t('pg.showroom.managerTitle')}
            </h1>
            <p className="text-gray-600">{t('pg.showroom.managerSubtitle')}</p>
          </div>
          
          <Button disabled={!hasShowroomRecord} title={!hasShowroomRecord ? t('pca.sm.showroomRequiredHint') : undefined} onClick={() => setIsEditing(!isEditing)} variant={isEditing ? "outline" : "default"}>
            {isEditing ? (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t('pg.showroom.saveChanges')}
              </>
            ) : (
              <>
                <Settings className="mr-2 h-4 w-4" />
                {t('pg.showroom.editShowroom')}
              </>
            )}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Showroom Overview */}
            <Card>
              <CardHeader>
                <CardTitle>{t('pg.showroom.showroomInfo')}</CardTitle>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('fin.sm.businessName')}</label>
                      <Input
                        value={showroomForm.name}
                        onChange={(e) => setShowroomForm(prev => ({...prev, name: e.target.value}))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">{t('fin.sm.description')}</label>
                      <Textarea
                        value={showroomForm.description}
                        onChange={(e) => setShowroomForm(prev => ({...prev, description: e.target.value}))}
                        rows={4}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1">{t('fin.sm.address')}</label>
                      <div className="flex space-x-2">
                        <Input
                          value={showroomForm.location.address}
                          onChange={(e) => setShowroomForm(prev => ({
                            ...prev, 
                            location: {...prev.location, address: e.target.value}
                          }))}
                          className="flex-1"
                        />
                        <Button type="button" onClick={handleLocationLookup} variant="outline">
                          <MapPin className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">{t('pg.showroom.email')}</label>
                        <Input
                          type="email"
                          value={showroomForm.contact_options.email}
                          onChange={(e) => setShowroomForm(prev => ({
                            ...prev,
                            contact_options: {...prev.contact_options, email: e.target.value}
                          }))}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">{t('pg.showroom.phone')}</label>
                        <Input
                          value={showroomForm.contact_options.phone}
                          onChange={(e) => setShowroomForm(prev => ({
                            ...prev,
                            contact_options: {...prev.contact_options, phone: e.target.value}
                          }))}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
                        {t('common.cancel')}
                      </Button>
                      <Button type="submit" disabled={loading}>
                        {t('pg.showroom.saveChanges')}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-xl font-semibold">{showroom?.name}</h3>
                      <p className="text-gray-600 mt-2">{showroom?.description}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-start space-x-3">
                        <MapPin className="h-5 w-5 text-gray-400 mt-1" />
                        <div>
                          <p className="font-medium">{t('pg.showroom.location')}</p>
                          <p className="text-sm text-gray-600">{showroom?.location?.address || t('pca.sm.noAddressProvided')}</p>
                        </div>
                      </div>

                      <div className="space-y-3">
                        {showroom?.contact_options?.email && (
                          <div className="flex items-center space-x-3">
                            <Mail className="h-5 w-5 text-gray-400" />
                            <span className="text-sm">{showroom.contact_options.email}</span>
                          </div>
                        )}
                        
                        {showroom?.contact_options?.phone && (
                          <div className="flex items-center space-x-3">
                            <Phone className="h-5 w-5 text-gray-400" />
                            <span className="text-sm">{showroom.contact_options.phone}</span>
                          </div>
                        )}

                        <div className="flex space-x-2">
                          {showroom?.contact_options?.chat && (
                            <Badge variant="secondary">
                              <MessageCircle className="h-3 w-3 mr-1" />
                              {t('pca.sm.chat')}
                            </Badge>
                          )}
                          {showroom?.contact_options?.in_app_call && (
                            <Badge variant="secondary">
                              <Phone className="h-3 w-3 mr-1" />
                              {t('pca.sm.inAppCalls')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Products Management — uses Tabs/TabsContent/TabsList/TabsTrigger for filtering */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{t('pg.showroom.productsServices')}</CardTitle>
                  <Button size="sm" disabled={!hasShowroomRecord} title={!hasShowroomRecord ? t('pca.sm.showroomRequiredHint') : undefined} onClick={() => setAddProductOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('pca.sm.addShowroomItem')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={productTab} onValueChange={setProductTab} className="mb-4">
                  <TabsList>
                    <TabsTrigger value="all">{t('pg.showroom.all')} ({(products || []).length})</TabsTrigger>
                    <TabsTrigger value="active">{t('pg.showroom.active')} ({(products || []).filter(p => p.is_active !== false).length})</TabsTrigger>
                    <TabsTrigger value="hidden">{t('pg.showroom.hidden')} ({(products || []).filter(p => p.is_active === false).length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value={productTab}>
                    {filteredProducts.length > 0 ? (
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {filteredProducts.map((product) => (
                          <div key={product.id} className="flex items-center justify-between p-4 border rounded-lg">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <h4 className="font-medium">{product.name}</h4>
                                <Badge variant={product.is_active !== false ? 'default' : 'secondary'}>
                                  {product.is_active !== false ? t('pg.showroom.active') : t('pg.showroom.hidden')}
                                </Badge>
                                {product.favorites_count > 0 && (
                                  <Badge variant="outline" className="text-pink-600 border-pink-300">
                                    <Heart className="h-3 w-3 mr-1 fill-pink-500 text-pink-500" />{product.favorites_count}
                                  </Badge>
                                )}
                              </div>
                              {/* Governance correction request. The listing stays PUBLICLY VISIBLE
                                  (advisory policy) — this is an obligation on the tenant, not a
                                  removal. Servisfy never edits tenant copy; the Business fixes it
                                  here and saving advances the state to awaiting review. */}
                              {product.moderation_review?.correction_state
                                && product.moderation_review.correction_state !== 'accepted' && (
                                <div className={'mb-2 p-3 rounded border text-sm ' + (
                                  product.moderation_review.correction_state === 'awaiting_review'
                                    ? 'bg-blue-50 border-blue-200 text-blue-800'
                                    : 'bg-amber-50 border-amber-200 text-amber-900')}>
                                  <div className="font-semibold">
                                    {product.moderation_review.correction_state === 'awaiting_review'
                                      ? t('pca.sm.correctionAwaitingReview')
                                      : t('pca.sm.correctionRequested')}
                                  </div>
                                  {product.moderation_review.reason && (
                                    <div className="mt-1">{product.moderation_review.reason}</div>
                                  )}
                                  {product.moderation_review.reviewed_at && (
                                    <div className="mt-1 text-xs opacity-80">
                                      {t('pca.sm.correctionRequestedAt')} {String(product.moderation_review.reviewed_at).slice(0, 10)}
                                    </div>
                                  )}
                                  <div className="mt-1 text-xs opacity-80">
                                    {product.moderation_review.correction_state === 'awaiting_review'
                                      ? t('pca.sm.correctionSubmittedHint')
                                      : t('pca.sm.correctionEditHint')}
                                  </div>
                                </div>
                              )}
                              {/* Settled, not outstanding: a one-line confirmation instead of an
                                  obligation banner. It carries no reason — the correction is closed,
                                  and repeating what was once wrong with the listing serves nobody. */}
                              {product.moderation_review?.correction_state === 'accepted' && (
                                <div className="mb-2 p-2 rounded border text-xs bg-green-50 border-green-200 text-green-800">
                                  {t('pca.sm.correctionAccepted')}
                                </div>
                              )}
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{product.description}</p>
                              
                              <div className="flex items-center space-x-4 text-sm">
                                {product.pricing && (
                                  <span className="font-semibold text-blue-600">
                                    {product.pricing.type === 'single' && `$${product.pricing.value}`}
                                    {product.pricing.type === 'contact' && t('pca.sm.contactForPrice')}
                                  </span>
                                )}
                                
                                <div className="flex items-center space-x-1 text-gray-500">
                                  <Eye className="h-3 w-3" />
                                  <span>{product.views || 0}</span>
                                </div>
                                
                                {product.images && product.images.length > 0 && (
                                  <div className="flex items-center space-x-1 text-gray-500">
                                    <Camera className="h-3 w-3" />
                                    <span>{product.images.length}</span>
                                  </div>
                                )}

                                {product.video && (
                                  <div className="flex items-center space-x-1 text-gray-500" title={t('pca.sm.productVideoTitle')}>
                                    <Video className="h-3 w-3" />
                                    <span>1</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex flex-col space-y-1 ml-4">
                              <div className="flex space-x-1">
                                <Button size="xs" variant="outline" onClick={() => handleEditProduct(product)}>
                                  <Edit className="h-3 w-3" />
                                </Button>
                                
                                <Button
                                  size="xs"
                                  variant={product.is_active !== false ? 'default' : 'outline'}
                                  onClick={() => handleToggleActive(product)}
                                >
                                  {product.is_active !== false ? t('pca.sm.unpublish') : t('pca.sm.publish')}
                                </Button>
                              </div>

                              <div className="flex space-x-1">
                                <Button
                                  size="xs"
                                  variant="destructive"
                                  onClick={() => handleDeleteProduct(product)}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                          <Store className="h-full w-full" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          {productTab === 'all' ? t('pca.sm.noItemsYet') : productTab === 'active' ? t('pg.showroom.noActiveProducts') : t('pg.showroom.noHiddenProducts')}
                        </h3>
                        <p className="text-gray-600 mb-4">{t('pg.showroom.startShowcasing')}</p>
                        <Button disabled={!hasShowroomRecord} title={!hasShowroomRecord ? t('pca.sm.showroomRequiredHint') : undefined} onClick={() => setAddProductOpen(true)}>
                          <Plus className="h-4 w-4 mr-2" />
                          {t('pca.sm.addShowroomItem')}
                        </Button>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Analytics Overview */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="mr-2 h-5 w-5" />
                  {t('pg.showroom.analyticsOverview')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Eye className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-600">{t('pg.showroom.totalViews')}</span>
                  </div>
                  <span className="font-semibold">{analytics.totalViews.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Store className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-600">{t('pg.showroom.products')}</span>
                  </div>
                  <span className="font-semibold">{analytics.activeProducts}/{analytics.totalProducts}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Star className="h-4 w-4 text-yellow-600" />
                    <span className="text-sm text-gray-600">{t('pg.showroom.rating')}</span>
                  </div>
                  <span className="font-semibold">{analytics.avgRating}/5</span>
                </div>

                <Button variant="outline" className="w-full mt-4" onClick={handleViewAnalytics}>
                  <TrendingUp className="h-4 w-4 mr-2" />
                  {t('pg.showroom.viewFullAnalytics')}
                </Button>
              </CardContent>
            </Card>

            {/* Quick Actions — uses DialogTrigger for "Upload to Showroom" */}
            <Card>
              <CardHeader>
                <CardTitle>{t('fin.common.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button variant="outline" className="w-full justify-start" disabled={!hasShowroomRecord} title={!hasShowroomRecord ? t('pca.sm.showroomRequiredHint') : undefined} onClick={() => setAddProductOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('pca.sm.addShowroomItem')}
                </Button>
                
                {/* DialogTrigger used here for Upload Images dialog */}
                <Dialog open={uploadImagesOpen} onOpenChange={setUploadImagesOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="h-4 w-4 mr-2" />
                      {t('pca.sm.uploadToShowroom')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('pca.sm.uploadShowroomImages')}</DialogTitle>
                      <DialogDescription>{t('pca.sm.addPhotosGallery')}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <Camera className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                        <p className="text-sm text-gray-600 mb-3">{t('pca.sm.dragDropImages')}</p>
                        <Button variant="outline" onClick={handleUploadImages}>
                          <Upload className="h-4 w-4 mr-2" />{t('pca.sm.selectFiles')}
                        </Button>
                      </div>
                      <Button className="w-full" onClick={() => { handleUploadImages(); setUploadImagesOpen(false); }}>
                        {t('pca.sm.uploadAndClose')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                
                <Button variant="outline" className="w-full justify-start" onClick={handleViewPublicProfile}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {t('pca.sm.viewPublicProfile')}
                </Button>

                <Button variant="outline" className="w-full justify-start" onClick={handleCustomerInquiries}>
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {t('pca.sm.customerInquiries')}
                </Button>
              </CardContent>
            </Card>

            {/* Performance Tips */}
            <Card>
              <CardHeader>
                <CardTitle>{t('pca.sm.performanceTips')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="font-medium text-blue-900">{t('pca.sm.addMoreImages')}</p>
                    <p className="text-blue-700">{t('pca.sm.images40more')}</p>
                    <Button size="sm" className="mt-2" variant="outline" onClick={handleUploadImages}>
                      <Upload className="h-3 w-3 mr-1" />
                      {t('pca.sm.uploadImages')}
                    </Button>
                  </div>

                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-medium text-green-900">{t('pca.sm.updateDescriptions')}</p>
                    <p className="text-green-700">{t('pca.sm.detailedDescImprove')}</p>
                    <Button size="sm" className="mt-2" variant="outline" onClick={handleUpdateDescriptions}>
                      <Edit className="h-3 w-3 mr-1" />
                      {t('pca.sm.editDescriptions')}
                    </Button>
                  </div>

                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <p className="font-medium text-purple-900">{t('pca.sm.respondToReviews')}</p>
                    <p className="text-purple-700">{t('pca.sm.activeEngagement')}</p>
                    <Button size="sm" className="mt-2" variant="outline" onClick={handleRespondToReviews}>
                      <MessageCircle className="h-3 w-3 mr-1" />
                      {t('pca.sm.viewReviews')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Create Showroom Dialog — uses showCreateDialog state */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pca.sm.createNewShowroom')}</DialogTitle>
            <DialogDescription>{t('pca.sm.quickCreateShowroom')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('pg.showroom.businessNameReq')}</label>
              <Input
                value={showroomForm.name}
                onChange={(e) => setShowroomForm(prev => ({...prev, name: e.target.value}))}
                placeholder={t('pg.showroom.enterBusinessName')}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('pg.showroom.descriptionReq')}</label>
              <Textarea
                value={showroomForm.description}
                onChange={(e) => setShowroomForm(prev => ({...prev, description: e.target.value}))}
                placeholder={t('pg.showroom.describeBusiness')}
                rows={3}
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>{t('common.cancel')}</Button>
              <Button type="submit" disabled={loading}>{loading ? t('pca.sm.creating') : t('pca.sm.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={addProductOpen} onOpenChange={setAddProductOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pca.sm.addShowroomItem')}</DialogTitle>
            <DialogDescription>
              {t('pca.sm.addShowroomItemDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
            {/* Item type is real domain data (persisted as item_type), not a label swap. A clinic
                publishing a consultation has no product to photograph, and calling it one made the
                whole flow retail-biased. Chosen FIRST because it narrows the category list. */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('pca.sm.itemTypeReq')}</label>
              <div className="flex gap-2">
                {ITEM_TYPES.map((it) => (
                  <Button
                    key={it.id}
                    type="button"
                    variant={productForm.itemType === it.id ? 'default' : 'outline'}
                    onClick={() => setProductForm(prev => ({
                      ...prev,
                      itemType: it.id,
                      // Drop a category that the newly chosen type cannot use, rather than silently
                      // submitting a pair the backend will reject.
                      category: (() => {
                        const c = categories.find(x => x.id === prev.category);
                        if (!c) return prev.category;
                        const a = c.applicability;
                        return (!a || a === 'both' || a === it.id) ? prev.category : '';
                      })(),
                    }))}
                  >
                    {t(it.labelKey)}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {productForm.itemType === 'service' ? t('pca.sm.serviceNameReq')
                  : productForm.itemType === 'product' ? t('pca.sm.productNameReq')
                  : t('pca.sm.itemNameReq')}
              </label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm(prev => ({...prev, name: e.target.value}))}
                placeholder={t('pca.sm.enterProductName')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('pca.sm.descriptionReq')}</label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({...prev, description: e.target.value}))}
                placeholder={t('pca.sm.describeProductService')}
                rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('pca.sm.price')}</label>
                <Input
                  value={productForm.price}
                  onChange={(e) => setProductForm(prev => ({...prev, price: e.target.value}))}
                  placeholder="$0.00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('pca.sm.categoryReq')}</label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm(prev => ({...prev, category: e.target.value}))}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">{t('pca.sm.selectCategory')}</option>
                  {categories
                    .filter((c) => !productForm.itemType
                      || !c.applicability || c.applicability === 'both'
                      || c.applicability === productForm.itemType)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>
            </div>
            
            {/* Product Images Upload Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium mb-1">{t('pca.sm.itemImagesOptional')}</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="space-y-2">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleProductImageUpload}
                      disabled={uploadingImages}
                      className="mb-2"
                    >
                      {uploadingImages ? t('pca.sm.uploading') : t('pca.sm.uploadImages')}
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>{t('pca.sm.upTo10Images')}</p>
                    <p>{t('pca.sm.images40more')}</p>
                    <p>{t('pca.sm.recommendedFormat')}</p>
                  </div>
                </div>
                
                {productForm.imagePreviews && productForm.imagePreviews.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-green-600 mb-2">
                      {t('pca.sm.imagesUploadedCount', { count: productForm.imagePreviews.length })}
                    </p>
                    <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {productForm.imagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={preview}
                            alt={`Product ${index + 1}`}
                            className="w-full h-16 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => setProductForm(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index),
                              imagePreviews: prev.imagePreviews.filter((_, i) => i !== index)
                            }))}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Video Upload Section (single, optional, MP4/MOV) */}
            <div className="space-y-3">
              <label className="block text-sm font-medium mb-1">{t('pca.sm.productVideo')} <span className="text-gray-400 font-normal">{t('pca.sm.optional')}</span></label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="space-y-2">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleProductVideoUpload}
                      disabled={uploadingVideo}
                      className="mb-2"
                    >
                      {uploadingVideo ? t('pca.sm.uploading') : t('pca.sm.uploadProductVideo')}
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>{t('pca.sm.oneVideoPerProduct')}</p>
                    <p>{t('pca.sm.videoFormatMax')}</p>
                  </div>
                </div>

                {productForm.videoPreview && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-green-600 mb-2">{t('pca.sm.videoUploadedLabel')}</p>
                    <div className="relative inline-block">
                      <video
                        src={productForm.videoPreview}
                        controls
                        className="max-h-40 rounded"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveProductVideo}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        x
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setAddProductOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleAddProduct}>
                {t('pca.sm.addShowroomItem')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Analytics Dialog */}
      <Dialog open={analyticsOpen} onOpenChange={setAnalyticsOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{t('pca.sm.showroomAnalytics')}</DialogTitle>
            <DialogDescription>
              {t('pca.sm.detailedMetrics')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Eye className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-gray-600">{t('pg.showroom.totalViews')}</p>
                      <p className="text-2xl font-bold">{analytics.totalViews.toLocaleString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm text-gray-600">{t('pca.sm.visitors')}</p>
                      <p className="text-2xl font-bold">—</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5 text-purple-600" />
                    <div>
                      <p className="text-sm text-gray-600">{t('pca.sm.inquiries')}</p>
                      <p className="text-2xl font-bold">{customerInquiries.length}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2">
                    <Star className="h-5 w-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-gray-600">{t('pg.showroom.rating')}</p>
                      <p className="text-2xl font-bold">{analytics.avgRating}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="text-center py-8">
              <p className="text-gray-500">{t('pca.sm.analyticsNotAvailable')}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Inquiries Dialog */}
      <Dialog open={inquiriesOpen} onOpenChange={isProcessingReply ? undefined : setInquiriesOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pca.sm.customerInquiries')}</DialogTitle>
            <DialogDescription>
              {t('pca.sm.manageInquiries')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {customerInquiries.map((inquiry) => (
              <div key={`${inquiry.id}-${inquiry.status}-${inquiry.repliedAt || 'new'}-${refreshTrigger}`} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium">{inquiry.customer}</h4>
                      <Badge variant={inquiry.status === 'pending' ? 'destructive' : 'secondary'} className={inquiry.status === 'replied' ? 'bg-green-100 text-green-800 border-green-200' : ''}>
                        {inquiry.status === 'replied' ? t('pca.sm.replied') : inquiry.status}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">"{inquiry.message}"</p>
                    <p className="text-xs text-gray-500">
                      {t('pca.sm.from')} {inquiry.email} | {inquiry.date}
                    </p>
                  </div>
                </div>
                
                {/* Display reply if inquiry has been replied to */}
                {inquiry.status === 'replied' && inquiry.reply && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-green-800 mb-1">{t('pca.sm.yourReply')}</p>
                        <p className="text-sm text-green-700">"{inquiry.reply}"</p>
                        <p className="text-xs text-green-600 mt-1">
                          {t('pca.sm.repliedOn')} {inquiry.repliedAt ? new Date(inquiry.repliedAt).toLocaleDateString() : t('pca.sm.unknown')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedInquiry === inquiry.id ? (
                  <div className="mt-3 space-y-3 bg-gray-50 p-3 rounded">
                    <Textarea
                      value={inquiryReply}
                      onChange={(e) => setInquiryReply(e.target.value)}
                      placeholder={t('pca.sm.typeReply')}
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {setSelectedInquiry(null); setInquiryReply('');}}
                        disabled={isProcessingReply}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReplyToInquiry(inquiry.id)}
                        disabled={isProcessingReply}
                      >
                        {isProcessingReply ? t('pca.sm.sending') : t('pca.sm.sendReply')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button 
                      size="sm" 
                      onClick={() => setSelectedInquiry(inquiry.id)}
                      variant={inquiry.status === 'pending' ? 'default' : 'outline'}
                    >
                      {inquiry.status === 'pending' ? t('pca.sm.reply') : t('pca.sm.replyAgain')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            {customerInquiries.length === 0 && (
              <div className="text-center py-8">
                <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">{t('pca.sm.noInquiriesYet')}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Reviews Dialog */}
      <Dialog open={reviewsOpen} onOpenChange={isProcessingResponse ? undefined : setReviewsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pca.sm.customerReviews')}</DialogTitle>
            <DialogDescription>
              {t('pca.sm.respondFeedback')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {customerReviews.map((review) => (
              <div key={`${review.id}-${review.responded}-${review.respondedAt || 'new'}-${refreshTrigger}`} className="border rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium">{review.customer}</h4>
                      <div className="flex items-center">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <Badge variant={review.responded ? 'default' : 'secondary'} className={review.responded ? 'bg-blue-100 text-blue-800 border-blue-200' : 'bg-gray-100 text-gray-800'}>
                        {review.responded ? t('pca.sm.responded') : t('pca.sm.new')}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">"{review.review}"</p>
                    <p className="text-xs text-gray-500">{review.date}</p>
                  </div>
                </div>
                
                {/* Display response if review has been responded to */}
                {review.responded && review.response && (
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                    <div className="flex items-start space-x-2">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-blue-800 mb-1">{t('pca.sm.yourResponse')}</p>
                        <p className="text-sm text-blue-700">"{review.response}"</p>
                        <p className="text-xs text-blue-600 mt-1">
                          {t('pca.sm.respondedOn')} {review.respondedAt ? new Date(review.respondedAt).toLocaleDateString() : t('pca.sm.unknown')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {selectedReview === review.id ? (
                  <div className="mt-3 space-y-3 bg-gray-50 p-3 rounded">
                    <Textarea
                      value={reviewResponse}
                      onChange={(e) => setReviewResponse(e.target.value)}
                      placeholder={t('pca.sm.thankCustomer')}
                      rows={3}
                    />
                    <div className="flex justify-end space-x-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => {setSelectedReview(null); setReviewResponse('');}}
                        disabled={isProcessingResponse}
                      >
                        {t('common.cancel')}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleRespondToReview(review.id)}
                        disabled={isProcessingResponse}
                      >
                        {isProcessingResponse ? t('pca.sm.posting') : t('pca.sm.postResponse')}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-end">
                    <Button 
                      size="sm" 
                      onClick={() => setSelectedReview(review.id)}
                      variant={!review.responded ? 'default' : 'outline'}
                    >
                      {review.responded ? t('pca.sm.updateResponse') : t('pca.sm.respond')}
                    </Button>
                  </div>
                )}
              </div>
            ))}
            
            {customerReviews.length === 0 && (
              <div className="text-center py-8">
                <Star className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">{t('pca.sm.noReviewsYet')}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={editProductOpen} onOpenChange={setEditProductOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('pca.sm.editShowroomItem')}</DialogTitle>
            <DialogDescription>
              {t('pca.sm.updateItemInfo')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
            {/* Item type on EDIT is what lets a tenant classify a listing that predates the field —
                the platform never does it for them. */}
            <div>
              <label className="block text-sm font-medium mb-1">{t('pca.sm.itemTypeReq')}</label>
              <div className="flex gap-2">
                {ITEM_TYPES.map((it) => (
                  <Button
                    key={it.id}
                    type="button"
                    variant={productForm.itemType === it.id ? 'default' : 'outline'}
                    onClick={() => setProductForm(prev => ({
                      ...prev,
                      itemType: it.id,
                      category: (() => {
                        const c = categories.find(x => x.id === prev.category);
                        if (!c) return prev.category;
                        const a = c.applicability;
                        return (!a || a === 'both' || a === it.id) ? prev.category : '';
                      })(),
                    }))}
                  >
                    {t(it.labelKey)}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {productForm.itemType === 'service' ? t('pca.sm.serviceNameReq')
                  : productForm.itemType === 'product' ? t('pca.sm.productNameReq')
                  : t('pca.sm.itemNameReq')}
              </label>
              <Input
                placeholder={t('pca.sm.enterProductName')}
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('pca.sm.descriptionReq')}</label>
              <Textarea
                placeholder={t('pca.sm.describeProduct')}
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('pca.sm.price')}</label>
                <Input
                  placeholder="0.00"
                  value={productForm.price}
                  onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('pca.sm.categoryReq')}</label>
                <select
                  value={productForm.category}
                  onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                >
                  <option value="">{t('pca.sm.selectCategory')}</option>
                  {categories
                    .filter((c) => !productForm.itemType
                      || !c.applicability || c.applicability === 'both'
                      || c.applicability === productForm.itemType)
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
              </div>
            </div>

            {/* Product Images Upload Section */}
            <div className="space-y-3">
              <label className="block text-sm font-medium mb-1">{t('pca.sm.itemImagesOptional')}</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="space-y-2">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleProductImageUpload}
                      disabled={uploadingImages}
                      className="mb-2"
                    >
                      {uploadingImages ? t('pca.sm.uploading') : t('pca.sm.uploadImages')}
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>{t('pca.sm.upTo10Images')}</p>
                    <p>{t('pca.sm.images40more')}</p>
                  </div>
                </div>

                {/* Existing product images (read-only preview). Uploading new images replaces this set. */}
                {(!productForm.imagePreviews || productForm.imagePreviews.length === 0) &&
                  selectedProduct?.images && selectedProduct.images.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-600 mb-2">
                      {t('pca.sm.currentImagesReplace', { count: selectedProduct.images.length })}
                    </p>
                    <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {selectedProduct.images.map((src, index) => (
                        <img
                          key={index}
                          src={src}
                          alt={`Current ${index + 1}`}
                          className="w-full h-16 object-cover rounded"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {productForm.imagePreviews && productForm.imagePreviews.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-green-600 mb-2">
                      {t('pca.sm.imagesUploadedCount', { count: productForm.imagePreviews.length })}
                    </p>
                    <div className="grid grid-cols-3 gap-2 max-h-32 overflow-y-auto">
                      {productForm.imagePreviews.map((preview, index) => (
                        <div key={index} className="relative">
                          <img
                            src={preview}
                            alt={`Product ${index + 1}`}
                            className="w-full h-16 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => setProductForm(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index),
                              imagePreviews: prev.imagePreviews.filter((_, i) => i !== index)
                            }))}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          >
                            x
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Video Upload Section (single, optional, MP4/MOV) */}
            <div className="space-y-3">
              <label className="block text-sm font-medium mb-1">{t('pca.sm.productVideo')} <span className="text-gray-400 font-normal">{t('pca.sm.optional')}</span></label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="space-y-2">
                  <Upload className="h-8 w-8 text-gray-400 mx-auto" />
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleProductVideoUpload}
                      disabled={uploadingVideo}
                      className="mb-2"
                    >
                      {uploadingVideo ? t('pca.sm.uploading') : (productForm.videoPreview || (selectedProduct?.video && !videoRemoved) ? t('pca.sm.replaceProductVideo') : t('pca.sm.uploadProductVideo'))}
                    </Button>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p>{t('pca.sm.oneVideoPerProduct')}</p>
                    <p>{t('pca.sm.videoFormatMax')}</p>
                  </div>
                </div>

                {productForm.videoPreview ? (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-green-600 mb-2">{t('pca.sm.newVideoUploaded')}</p>
                    <div className="relative inline-block">
                      <video src={productForm.videoPreview} controls className="max-h-40 rounded" />
                      <button
                        type="button"
                        onClick={handleRemoveProductVideo}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                      >
                        x
                      </button>
                    </div>
                  </div>
                ) : (selectedProduct?.video && !videoRemoved) ? (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-600 mb-2">{t('pca.sm.currentVideo')}</p>
                    <div className="relative inline-block">
                      <video src={selectedProduct.video} controls className="max-h-40 rounded" />
                      <button
                        type="button"
                        onClick={handleRemoveProductVideo}
                        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                        title={t('pca.sm.removeVideo')}
                      >
                        x
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setEditProductOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleUpdateProduct}>
                {t('pca.sm.updateProduct')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShowroomManager;